// DESCRIPTION: Decorative right-column panel for the setup wizard. Each step
// shows a distinct themed background with a small booking-related mockup.

import { __ } from '@wordpress/i18n';

// ─── Shared keyframes injected once ───────────────────────────────────────────

const SHAPE_STYLES = `
@keyframes nb-drift-a {
    0%   { transform: translate(0, 0) rotate(0deg); }
    33%  { transform: translate(10px, -14px) rotate(8deg); }
    66%  { transform: translate(-8px, 6px) rotate(-5deg); }
    100% { transform: translate(0, 0) rotate(0deg); }
}
@keyframes nb-drift-b {
    0%   { transform: translate(0, 0) rotate(45deg); }
    40%  { transform: translate(-12px, 10px) rotate(55deg); }
    75%  { transform: translate(8px, -8px) rotate(38deg); }
    100% { transform: translate(0, 0) rotate(45deg); }
}
@keyframes nb-drift-c {
    0%   { transform: translate(0, 0) rotate(30deg); }
    50%  { transform: translate(14px, 10px) rotate(42deg); }
    100% { transform: translate(0, 0) rotate(30deg); }
}
@keyframes nb-pulse-blob {
    0%, 100% { transform: scale(1); opacity: 1; }
    50%       { transform: scale(1.08); opacity: 0.8; }
}
`;

let _stylesInjected = false;
function injectStyles() {
	if ( _stylesInjected ) {
		return;
	}
	_stylesInjected = true;
	const el = document.createElement( 'style' );
	el.textContent = SHAPE_STYLES;
	document.head.appendChild( el );
}

// ─── Welcome — cream + booking page browser mockup ───────────────────────────

/**
 * Cream "booking page" mockup card used in the welcome panel.
 *
 * @since PBOOKING_SINCE
 *
 * @return {JSX.Element}
 */
function CreamPanel() {
	return (
		<div
			style={ {
				width: '100%',
				height: '100%',
				background:
					'linear-gradient(160deg, #faf7f2 0%, #f3ecde 60%, #ebe1cd 100%)',
				display: 'flex',
				flexDirection: 'column',
				alignItems: 'center',
				justifyContent: 'center',
				padding: '56px 48px',
				position: 'relative',
				overflow: 'hidden',
			} }
		>
			<div
				style={ {
					position: 'absolute',
					top: '-60px',
					right: '-60px',
					width: '260px',
					height: '260px',
					borderRadius: '50%',
					background: 'rgba(210,185,145,0.2)',
					pointerEvents: 'none',
				} }
			/>
			<div
				style={ {
					position: 'absolute',
					bottom: '-40px',
					left: '-40px',
					width: '200px',
					height: '200px',
					borderRadius: '50%',
					background: 'rgba(185,160,120,0.14)',
					pointerEvents: 'none',
				} }
			/>

			<div
				style={ {
					width: '100%',
					maxWidth: '420px',
					position: 'relative',
					zIndex: 1,
				} }
			>
				<div style={ { textAlign: 'center', marginBottom: '24px' } }>
					<div
						style={ {
							fontSize: '11px',
							fontWeight: 700,
							letterSpacing: '0.12em',
							textTransform: 'uppercase',
							color: '#92632a',
							marginBottom: '10px',
						} }
					>
						{ __( 'A booking page in minutes', 'nemtly-booking' ) }
					</div>
					<h3
						style={ {
							margin: '0 0 10px',
							fontSize: '22px',
							fontWeight: 700,
							color: '#2d2416',
							letterSpacing: '-0.01em',
							lineHeight: 1.25,
						} }
					>
						{ __(
							'A fully customizable booking calendar.',
							'nemtly-booking'
						) }
					</h3>
					<p
						style={ {
							margin: 0,
							fontSize: '13px',
							lineHeight: 1.55,
							color: '#7a6b50',
						} }
					>
						{ __(
							'Pick your colors, fonts, and fields. Customers see a clean booking experience that matches your brand.',
							'nemtly-booking'
						) }
					</p>
				</div>

				<div
					style={ {
						width: '100%',
						background: '#fff',
						borderRadius: '14px',
						boxShadow: '0 12px 48px rgba(120,90,40,0.13)',
						overflow: 'hidden',
						border: '1px solid #ede8df',
					} }
				>
					<div
						style={ {
							background: '#f5f0e8',
							borderBottom: '1px solid #ede8df',
							padding: '10px 14px',
							display: 'flex',
							alignItems: 'center',
							gap: '7px',
						} }
					>
						<div
							style={ {
								width: '9px',
								height: '9px',
								borderRadius: '50%',
								background: '#e8a89a',
							} }
						/>
						<div
							style={ {
								width: '9px',
								height: '9px',
								borderRadius: '50%',
								background: '#e8cd8a',
							} }
						/>
						<div
							style={ {
								width: '9px',
								height: '9px',
								borderRadius: '50%',
								background: '#a8d8a8',
							} }
						/>
						<div
							style={ {
								flex: 1,
								marginLeft: '8px',
								background: '#ede8df',
								borderRadius: '5px',
								height: '20px',
								display: 'flex',
								alignItems: 'center',
								paddingLeft: '9px',
							} }
						>
							<span
								style={ { fontSize: '10px', color: '#b0a090' } }
							>
								yoursite.com/book
							</span>
						</div>
					</div>
					<div style={ { padding: '18px' } }>
						<div
							style={ {
								fontSize: '13px',
								fontWeight: 700,
								color: '#2d2416',
							} }
						>
							{ __( 'Book a session', 'nemtly-booking' ) }
						</div>
						<div
							style={ {
								fontSize: '11px',
								color: '#9c8f7a',
								marginTop: '2px',
							} }
						>
							{ __(
								'30-min Discovery Call · Free',
								'nemtly-booking'
							) }
						</div>

						<div
							style={ {
								marginTop: '14px',
								display: 'grid',
								gridTemplateColumns: 'repeat(4, 1fr)',
								gap: '6px',
							} }
						>
							{ [ 'Mon', 'Tue', 'Wed', 'Thu' ].map( ( d, i ) => (
								<div
									key={ d }
									style={ {
										border: '1px solid #ede8df',
										borderRadius: '6px',
										padding: '8px 0',
										textAlign: 'center',
										background:
											i === 1 ? '#4f46e5' : '#fff',
										color: i === 1 ? '#fff' : '#5c4a2a',
										fontSize: '10px',
										fontWeight: 600,
									} }
								>
									<div>{ d }</div>
									<div
										style={ {
											fontSize: '13px',
											marginTop: '2px',
										} }
									>
										{ 12 + i }
									</div>
								</div>
							) ) }
						</div>

						<div
							style={ {
								marginTop: '14px',
								display: 'grid',
								gridTemplateColumns: 'repeat(3, 1fr)',
								gap: '6px',
							} }
						>
							{ [
								'09:00',
								'10:30',
								'13:00',
								'14:00',
								'15:30',
								'16:30',
							].map( ( t, i ) => (
								<div
									key={ t }
									style={ {
										border:
											'1px solid ' +
											( i === 2 ? '#4f46e5' : '#ede8df' ),
										background:
											i === 2 ? '#eef0ff' : '#fff',
										color: i === 2 ? '#4f46e5' : '#5c4a2a',
										borderRadius: '6px',
										padding: '7px 0',
										textAlign: 'center',
										fontSize: '10px',
										fontWeight: 600,
									} }
								>
									{ t }
								</div>
							) ) }
						</div>

						<div
							style={ {
								marginTop: '14px',
								height: '32px',
								borderRadius: '7px',
								background: '#4f46e5',
								color: '#fff',
								display: 'flex',
								alignItems: 'center',
								justifyContent: 'center',
								fontSize: '11px',
								fontWeight: 700,
							} }
						>
							{ __( 'Confirm booking', 'nemtly-booking' ) }
						</div>
					</div>
				</div>

				<div
					style={ {
						display: 'flex',
						justifyContent: 'center',
						flexWrap: 'wrap',
						gap: '6px 14px',
						marginTop: '20px',
					} }
				>
					{ [
						__( 'Custom branding', 'nemtly-booking' ),
						__( 'Group & 1:1', 'nemtly-booking' ),
						__( 'Recurring slots', 'nemtly-booking' ),
						__( 'Mobile-friendly', 'nemtly-booking' ),
					].map( ( label ) => (
						<span
							key={ label }
							style={ {
								fontSize: '11px',
								fontWeight: 600,
								color: '#7a6b50',
								display: 'inline-flex',
								alignItems: 'center',
								gap: '5px',
							} }
						>
							<span
								style={ {
									width: '5px',
									height: '5px',
									borderRadius: '50%',
									background: '#c97c3a',
								} }
							/>
							{ label }
						</span>
					) ) }
				</div>
			</div>
		</div>
	);
}

// ─── Persona / type — indigo abstract with "events list" feature card ────────

function IndigoPanel() {
	injectStyles();
	return (
		<div
			style={ {
				width: '100%',
				height: '100%',
				background:
					'linear-gradient(145deg, #0f0c29 0%, #1a1060 50%, #24243e 100%)',
				display: 'flex',
				alignItems: 'center',
				justifyContent: 'center',
				padding: '48px',
				position: 'relative',
				overflow: 'hidden',
			} }
		>
			<div
				style={ {
					position: 'absolute',
					top: '8%',
					right: '6%',
					width: '180px',
					height: '180px',
					borderRadius: '40% 60% 70% 30% / 50% 40% 60% 50%',
					background: 'rgba(99,102,241,0.25)',
					filter: 'blur(2px)',
					pointerEvents: 'none',
					animation: 'nb-drift-a 14s ease-in-out infinite',
				} }
			/>
			<div
				style={ {
					position: 'absolute',
					bottom: '10%',
					left: '4%',
					width: '140px',
					height: '140px',
					borderRadius: '60% 40% 30% 70% / 60% 30% 70% 40%',
					background: 'rgba(139,92,246,0.2)',
					filter: 'blur(1px)',
					pointerEvents: 'none',
					animation: 'nb-pulse-blob 10s ease-in-out infinite',
				} }
			/>
			<div
				style={ {
					position: 'absolute',
					top: '50%',
					left: '12%',
					width: '80px',
					height: '80px',
					borderRadius: '50%',
					background: 'rgba(192,132,252,0.15)',
					pointerEvents: 'none',
					animation: 'nb-drift-a 18s ease-in-out infinite reverse',
				} }
			/>
			<div
				style={ {
					position: 'absolute',
					top: '20%',
					left: '18%',
					width: '20px',
					height: '20px',
					background: 'rgba(167,139,250,0.5)',
					transform: 'rotate(45deg)',
					borderRadius: '3px',
					pointerEvents: 'none',
					animation: 'nb-drift-b 12s ease-in-out infinite',
				} }
			/>
			<div
				style={ {
					position: 'absolute',
					bottom: '28%',
					right: '14%',
					width: '14px',
					height: '14px',
					background: 'rgba(99,102,241,0.6)',
					transform: 'rotate(45deg)',
					borderRadius: '2px',
					pointerEvents: 'none',
					animation: 'nb-drift-b 16s ease-in-out infinite reverse',
				} }
			/>

			<div
				style={ {
					width: '100%',
					maxWidth: '360px',
					position: 'relative',
					zIndex: 1,
				} }
			>
				<div
					style={ {
						background: 'rgba(255,255,255,0.06)',
						backdropFilter: 'blur(12px)',
						border: '1px solid rgba(255,255,255,0.12)',
						borderRadius: '16px',
						padding: '28px',
					} }
				>
					<div
						style={ {
							fontSize: '11px',
							fontWeight: 700,
							letterSpacing: '0.1em',
							color: 'rgba(167,139,250,0.9)',
							textTransform: 'uppercase',
							marginBottom: '20px',
						} }
					>
						{ __(
							'Built for every kind of booking',
							'nemtly-booking'
						) }
					</div>
					{ [
						{
							icon: '📅',
							label: __( '1:1 appointments', 'nemtly-booking' ),
							badge: __( 'Personal', 'nemtly-booking' ),
						},
						{
							icon: '👥',
							label: __( 'Group classes', 'nemtly-booking' ),
							badge: __( 'Capacity', 'nemtly-booking' ),
						},
						{
							icon: '🎟️',
							label: __( 'Ticketed events', 'nemtly-booking' ),
							badge: __( 'Sell out', 'nemtly-booking' ),
						},
						{
							icon: '🔁',
							label: __(
								'Recurring schedules',
								'nemtly-booking'
							),
							badge: __( 'Smart', 'nemtly-booking' ),
						},
					].map( ( row ) => (
						<div
							key={ row.label }
							style={ {
								display: 'flex',
								alignItems: 'center',
								gap: '10px',
								marginBottom: '12px',
							} }
						>
							<span
								style={ {
									fontSize: '16px',
									flexShrink: 0,
									lineHeight: 1,
								} }
							>
								{ row.icon }
							</span>
							<span
								style={ {
									flex: 1,
									fontSize: '13px',
									color: 'rgba(255,255,255,0.78)',
								} }
							>
								{ row.label }
							</span>
							<span
								style={ {
									fontSize: '10px',
									fontWeight: 700,
									padding: '3px 9px',
									borderRadius: '20px',
									background: 'rgba(167,139,250,0.2)',
									color: '#c4b5fd',
									flexShrink: 0,
								} }
							>
								{ row.badge }
							</span>
						</div>
					) ) }
				</div>
			</div>
		</div>
	);
}

// ─── Hours — warm slate + clock-themed visual ─────────────────────────────────

function ClockPanel() {
	injectStyles();
	return (
		<div
			style={ {
				width: '100%',
				height: '100%',
				background:
					'linear-gradient(155deg, #1e293b 0%, #312e4f 60%, #4338ca 100%)',
				display: 'flex',
				alignItems: 'center',
				justifyContent: 'center',
				padding: '48px',
				position: 'relative',
				overflow: 'hidden',
			} }
		>
			<div
				style={ {
					position: 'absolute',
					top: '-30px',
					right: '-30px',
					width: '220px',
					height: '220px',
					background: 'rgba(165,180,252,0.14)',
					borderRadius: '24px',
					pointerEvents: 'none',
					animation: 'nb-drift-c 16s ease-in-out infinite',
				} }
			/>
			<div
				style={ {
					position: 'absolute',
					bottom: '-20px',
					left: '-20px',
					width: '180px',
					height: '180px',
					background: 'rgba(99,102,241,0.18)',
					borderRadius: '16px',
					pointerEvents: 'none',
					animation: 'nb-drift-b 13s ease-in-out infinite',
				} }
			/>

			<div
				style={ {
					width: '100%',
					maxWidth: '340px',
					position: 'relative',
					zIndex: 1,
				} }
			>
				<div
					style={ {
						background: 'rgba(255,255,255,0.07)',
						backdropFilter: 'blur(12px)',
						border: '1px solid rgba(255,255,255,0.13)',
						borderRadius: '16px',
						padding: '24px',
					} }
				>
					<div
						style={ {
							fontSize: '11px',
							fontWeight: 700,
							letterSpacing: '0.1em',
							color: 'rgba(199,210,254,0.9)',
							textTransform: 'uppercase',
							marginBottom: '16px',
						} }
					>
						{ __( 'This week', 'nemtly-booking' ) }
					</div>

					{ [
						{ day: 'Mon', range: '09:00 – 17:00', count: 6 },
						{ day: 'Tue', range: '09:00 – 17:00', count: 4 },
						{ day: 'Wed', range: '09:00 – 13:00', count: 3 },
						{ day: 'Thu', range: '09:00 – 17:00', count: 7 },
						{ day: 'Fri', range: '09:00 – 15:00', count: 5 },
					].map( ( row ) => (
						<div
							key={ row.day }
							style={ {
								display: 'flex',
								alignItems: 'center',
								gap: '10px',
								padding: '8px 0',
								borderBottom:
									'1px solid rgba(255,255,255,0.06)',
							} }
						>
							<span
								style={ {
									width: '36px',
									fontSize: '12px',
									fontWeight: 700,
									color: '#fff',
								} }
							>
								{ row.day }
							</span>
							<span
								style={ {
									flex: 1,
									fontSize: '12px',
									color: 'rgba(255,255,255,0.7)',
								} }
							>
								{ row.range }
							</span>
							<span
								style={ {
									fontSize: '10px',
									padding: '3px 8px',
									borderRadius: '999px',
									background: 'rgba(165,180,252,0.2)',
									color: '#c7d2fe',
									fontWeight: 700,
								} }
							>
								{ row.count }{ ' ' }
								{ __( 'slots', 'nemtly-booking' ) }
							</span>
						</div>
					) ) }
				</div>
			</div>
		</div>
	);
}

// ─── Notifications — sage/teal with an inbox mockup ───────────────────────────

function InboxPanel() {
	return (
		<div
			style={ {
				width: '100%',
				height: '100%',
				background:
					'linear-gradient(155deg, #042f2e 0%, #115e59 60%, #0f766e 100%)',
				display: 'flex',
				alignItems: 'center',
				justifyContent: 'center',
				padding: '48px',
				position: 'relative',
				overflow: 'hidden',
			} }
		>
			<div
				style={ {
					position: 'absolute',
					top: '-40px',
					right: '-40px',
					width: '220px',
					height: '220px',
					borderRadius: '50%',
					background: 'rgba(94,234,212,0.18)',
					pointerEvents: 'none',
				} }
			/>
			<div
				style={ {
					position: 'absolute',
					bottom: '-30px',
					left: '-30px',
					width: '180px',
					height: '180px',
					borderRadius: '50%',
					background: 'rgba(45,212,191,0.12)',
					pointerEvents: 'none',
				} }
			/>

			<div
				style={ {
					width: '100%',
					maxWidth: '360px',
					position: 'relative',
					zIndex: 1,
				} }
			>
				<div
					style={ {
						background: 'rgba(255,255,255,0.08)',
						backdropFilter: 'blur(12px)',
						border: '1px solid rgba(255,255,255,0.14)',
						borderRadius: '16px',
						padding: '20px',
					} }
				>
					<div
						style={ {
							fontSize: '11px',
							fontWeight: 700,
							letterSpacing: '0.1em',
							color: 'rgba(153,246,228,0.9)',
							textTransform: 'uppercase',
							marginBottom: '16px',
						} }
					>
						{ __( 'New email', 'nemtly-booking' ) }
					</div>

					<div
						style={ {
							background: 'rgba(255,255,255,0.95)',
							borderRadius: '10px',
							padding: '14px',
							color: '#0f0f10',
						} }
					>
						<div
							style={ {
								fontSize: '12px',
								color: '#5b5b62',
								marginBottom: '4px',
							} }
						>
							{ __( 'From: Acme Studio', 'nemtly-booking' ) }
						</div>
						<div
							style={ {
								fontSize: '13px',
								fontWeight: 700,
								marginBottom: '8px',
							} }
						>
							{ __(
								'Your booking is confirmed',
								'nemtly-booking'
							) }
						</div>
						<div
							style={ {
								fontSize: '11px',
								color: '#5b5b62',
								lineHeight: 1.5,
							} }
						>
							{ __(
								'Hi! Just confirming your 30-min Discovery Call on Tuesday at 13:00. We will email a reminder 24 hours before.',
								'nemtly-booking'
							) }
						</div>
						<div
							style={ {
								marginTop: '10px',
								display: 'inline-block',
								padding: '4px 10px',
								borderRadius: '999px',
								background: '#eef0ff',
								color: '#4f46e5',
								fontSize: '10px',
								fontWeight: 700,
							} }
						>
							{ __( '✓ Confirmed', 'nemtly-booking' ) }
						</div>
					</div>
				</div>
			</div>
		</div>
	);
}

// ─── Payments — amber/terracotta with a card-payment mockup ──────────────────

function PaymentsPanel() {
	injectStyles();
	return (
		<div
			style={ {
				width: '100%',
				height: '100%',
				background:
					'linear-gradient(135deg, #451a03 0%, #7c2d12 45%, #92400e 100%)',
				display: 'flex',
				alignItems: 'center',
				justifyContent: 'center',
				padding: '48px',
				position: 'relative',
				overflow: 'hidden',
			} }
		>
			<div
				style={ {
					position: 'absolute',
					top: '-30px',
					right: '-30px',
					width: '220px',
					height: '220px',
					background: 'rgba(251,191,36,0.12)',
					borderRadius: '20px',
					pointerEvents: 'none',
					animation: 'nb-drift-c 16s ease-in-out infinite',
				} }
			/>
			<div
				style={ {
					position: 'absolute',
					bottom: '-20px',
					left: '-20px',
					width: '180px',
					height: '180px',
					background: 'rgba(234,88,12,0.18)',
					borderRadius: '16px',
					pointerEvents: 'none',
					animation: 'nb-drift-b 13s ease-in-out infinite',
				} }
			/>

			<div
				style={ {
					width: '100%',
					maxWidth: '360px',
					position: 'relative',
					zIndex: 1,
				} }
			>
				{ /* Card mockup */ }
				<div
					style={ {
						background:
							'linear-gradient(135deg, #1f1f23 0%, #2a2a32 100%)',
						borderRadius: '14px',
						padding: '20px',
						color: '#fff',
						boxShadow: '0 24px 48px -16px rgba(0,0,0,0.45)',
					} }
				>
					<div
						style={ {
							display: 'flex',
							justifyContent: 'space-between',
							alignItems: 'center',
							marginBottom: '32px',
						} }
					>
						<span
							style={ {
								fontSize: '11px',
								fontWeight: 700,
								letterSpacing: '0.1em',
								textTransform: 'uppercase',
								color: 'rgba(255,255,255,0.7)',
							} }
						>
							{ __( 'Pay securely', 'nemtly-booking' ) }
						</span>
						<span
							style={ {
								width: '30px',
								height: '20px',
								background:
									'linear-gradient(90deg, #fcd34d, #f59e0b)',
								borderRadius: '4px',
							} }
						/>
					</div>
					<div
						style={ {
							fontSize: '16px',
							letterSpacing: '0.18em',
							marginBottom: '20px',
							color: 'rgba(255,255,255,0.92)',
						} }
					>
						•••• •••• •••• 4242
					</div>
					<div
						style={ {
							display: 'flex',
							justifyContent: 'space-between',
							fontSize: '10px',
							color: 'rgba(255,255,255,0.6)',
						} }
					>
						<span>
							{ __( 'CARDHOLDER', 'nemtly-booking' ) }
							<br />
							<span style={ { color: '#fff', fontSize: '12px' } }>
								{ __( 'JANE DOE', 'nemtly-booking' ) }
							</span>
						</span>
						<span style={ { textAlign: 'right' } }>
							{ __( 'EXPIRES', 'nemtly-booking' ) }
							<br />
							<span style={ { color: '#fff', fontSize: '12px' } }>
								12/28
							</span>
						</span>
					</div>
				</div>

				{ /* Offline-alt card */ }
				<div
					style={ {
						marginTop: '14px',
						display: 'flex',
						alignItems: 'center',
						gap: '12px',
						padding: '14px',
						background: 'rgba(255,255,255,0.07)',
						border: '1px dashed rgba(253,186,116,0.4)',
						borderRadius: '12px',
					} }
				>
					<span style={ { fontSize: '20px' } }>💵</span>
					<span
						style={ {
							flex: 1,
							fontSize: '12px',
							color: 'rgba(255,255,255,0.85)',
						} }
					>
						{ __(
							'Prefer cash on arrival? That works too.',
							'nemtly-booking'
						) }
					</span>
				</div>
			</div>
		</div>
	);
}

// ─── Done — sage celebration ──────────────────────────────────────────────────

function CelebrationPanel() {
	return (
		<div
			style={ {
				width: '100%',
				height: '100%',
				background:
					'linear-gradient(155deg, #f0fdf4 0%, #dcfce7 50%, #d1fae5 100%)',
				display: 'flex',
				alignItems: 'center',
				justifyContent: 'center',
				padding: '48px',
				position: 'relative',
				overflow: 'hidden',
			} }
		>
			<div
				style={ {
					position: 'absolute',
					top: '-40px',
					right: '-40px',
					width: '220px',
					height: '220px',
					borderRadius: '50%',
					background: 'rgba(134,239,172,0.25)',
					pointerEvents: 'none',
				} }
			/>
			<div
				style={ {
					position: 'absolute',
					bottom: '-30px',
					left: '-30px',
					width: '180px',
					height: '180px',
					borderRadius: '50%',
					background: 'rgba(110,231,183,0.2)',
					pointerEvents: 'none',
				} }
			/>

			<div
				style={ {
					width: '100%',
					maxWidth: '360px',
					position: 'relative',
					zIndex: 1,
					textAlign: 'center',
				} }
			>
				<div
					style={ {
						width: '64px',
						height: '64px',
						borderRadius: '50%',
						background: '#fff',
						display: 'flex',
						alignItems: 'center',
						justifyContent: 'center',
						margin: '0 auto 20px',
						boxShadow: '0 4px 20px rgba(52,211,153,0.25)',
						fontSize: '28px',
						color: '#16794b',
					} }
				>
					✓
				</div>
				<div
					style={ {
						background: '#fff',
						borderRadius: '14px',
						padding: '24px',
						boxShadow: '0 4px 24px rgba(52,211,153,0.12)',
						border: '1px solid rgba(134,239,172,0.4)',
					} }
				>
					<div
						style={ {
							fontSize: '15px',
							fontWeight: 700,
							color: '#064e3b',
							marginBottom: '8px',
						} }
					>
						{ __( 'You are open for bookings.', 'nemtly-booking' ) }
					</div>
					<div
						style={ {
							fontSize: '12px',
							color: '#5b5b62',
							lineHeight: 1.6,
						} }
					>
						{ __(
							'Your booking page is live and a sample event is waiting for customers.',
							'nemtly-booking'
						) }
					</div>
				</div>
			</div>
		</div>
	);
}

// ─── Step → panel mapping ─────────────────────────────────────────────────────

const PANELS = {
	welcome: CreamPanel,
	persona: IndigoPanel,
	identity: IndigoPanel,
	hours: ClockPanel,
	notify: InboxPanel,
	payments: PaymentsPanel,
	finalizing: CelebrationPanel,
	done: CelebrationPanel,
};

/**
 * Decorative right-column panel. Renders the step-specific background.
 *
 * @since PBOOKING_SINCE
 *
 * @param {Object} props
 * @param {string} props.stepKey
 *
 * @return {JSX.Element}
 */
export default function PreviewPanel( { stepKey } ) {
	const Panel = PANELS[ stepKey ] || CreamPanel;
	return (
		<div style={ { height: '100%' } }>
			<Panel />
		</div>
	);
}
